import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  ButtonGroup,
  ThemeProvider,
  CircularProgress
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useDesignPatternStudio } from '../DesignPatternStudioContext';
import customTheme from '../../../customTheme';
import SvgUpload from '../../Signet/Studio/SvgUpload';
import { useHistory, useLocation } from 'react-router';
import { getSvgDimensionsAndPaths, processSvgContent } from '../../Signet/Studio/utils/svgUtils';
import PositionGrid from '../components/PositionGrid';
import CircularRange from '../../Signet/Studio/components/CircularRange';
import useDesignPatternAPI from '../hooks/useDesignPatternAPI';
import { extractSvgAttributes } from '../../Signet/Studio/utils/svgUtils';
import { SVG_SIZE } from '../../Signet/Studio/constants';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { getConfigSwitchLabel } from '../components/ApprovalMapping';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    margin: '0 auto',
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  controlsSection: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  svgContainer: {
    width: '100%',
    maxWidth: 500,
    aspectRatio: '1/1',
    margin: '0 auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    position: 'relative',
  },
  sliderContainer: {
    marginTop: theme.spacing(2),
  },
  sliderLabel: {
    marginBottom: theme.spacing(1),
  },
  formControl: {
    marginTop: theme.spacing(2),
    minWidth: 200,
  },
  previewContainer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  ringImage: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
  },
  uploadedSvg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0,
  },
  svgDimensions: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
  imagePreview: {
    width: '600px',
    height: '600px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '100%',
      height: '100%',
    },
  },
  widthUnit: {
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'lowercase',
  },
  activeButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  }
}));

const markersPositionGrid = [
  { value: -500, position: "0%" },
  { value: -450, position: "5%" },
  { value: -400, position: "10%" },
  { value: -350, position: "15%" },
  { value: -300, position: "20%" },
  { value: -250, position: "25%" },
  { value: -200, position: "30%" },
  { value: -150, position: "35%" },
  { value: -100, position: "40%" },
  { value: -50, position: "45%" },
  { value: 0, position: "50%" },
  { value: 50, position: "55%" },
  { value: 100, position: "60%" },
  { value: 150, position: "65%" },
  { value: 200, position: "70%" },
  { value: 250, position: "75%" },
  { value: 300, position: "80%" },
  { value: 350, position: "85%" },
  { value: 400, position: "90%" },
  { value: 450, position: "95%" },
  { value: 500, position: "100%" },
];

const EditConfig = () => {
  const classes = useStyles();
  const { state, dispatch } = useDesignPatternStudio();
  const { updateProfileConfig, loading, fetchProfilesData } = useDesignPatternAPI();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const profileSku = queryParams.get('profileSku');
  const widthSku = queryParams.get('widthSku');
  const source = queryParams.get('source');
  const [svgFile, setSvgFile] = useState(null);
  const [svgContent, setSvgContent] = useState(null);
  const configSwitchLabel = getConfigSwitchLabel(state);
  const availableProfiles = useMemo(() => state?.profiles?.map(p => ({
    sku_code: p.sku_code,
    name: p.name
  })) || [], [state.profiles]);

  const getProfileWidthOptions = useCallback((sku) =>  state?.profiles?.find(p => p.sku_code === sku)?.width_options?.map(c => c.width) || [], [state?.profiles]);

  const availableWidths = useMemo(() => getProfileWidthOptions(profileSku) || [], [getProfileWidthOptions, profileSku]);

  const widthMapping = useMemo(() => state?.profiles?.find(p => p.sku_code === profileSku)?.width_options?.find(c => c.width === parseInt(widthSku || 0, 10)), [state?.profiles, profileSku, widthSku]);

  useEffect(() => {
    fetchProfilesData();
  }, [fetchProfilesData]);

  const widthConfig = state.profileConfigs?.[profileSku]?.[widthSku];
  const config = widthConfig?.config?.[configSwitchLabel];
  useEffect(() => {
    if (!profileSku && availableProfiles.length > 0) {
      const newQueryParams = new URLSearchParams(location.search);
      newQueryParams.set('profileSku', availableProfiles[0].sku_code);
      history.push({
        pathname: location.pathname,
        search: newQueryParams.toString()
      });
    }
  }, [availableProfiles, profileSku, location.search, history, location.pathname]);

  // Initialize widthSku if not provided but availableWidths has options
  useEffect(() => {
    if (profileSku && !widthSku && availableWidths.length > 0) {
      const newQueryParams = new URLSearchParams(location.search);
      newQueryParams.set('widthSku', availableWidths[0]);
      history.push({
        pathname: location.pathname,
        search: newQueryParams.toString()
      });
    }

  }, [availableWidths, widthSku, profileSku, location.search, history, location.pathname]);

  const handleWidthChange = (value) => {
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.set('widthSku', value);
    history.push({
      pathname: location.pathname,
      search: newQueryParams.toString()
    });

    setSvgFile(null);
    setSvgContent(null);
  };

  const handleProfileChange = (value) => {
    const newQueryParams = new URLSearchParams(location.search);
    newQueryParams.set('profileSku', value);

    // Check if the selected width is available in the new profile
    const newProfileWidths = getProfileWidthOptions(value) || [];
    const currentWidthSku = queryParams.get('widthSku');

    // If current width is not available in new profile, select the first width
    if (newProfileWidths.length > 0 && !newProfileWidths.includes(Number(currentWidthSku))) {
      newQueryParams.set('widthSku', newProfileWidths[0]);
    } else if (newProfileWidths.length === 0) {
      // If no widths available, remove the width parameter
      newQueryParams.delete('widthSku');
    }

    history.push({
      pathname: location.pathname,
      search: newQueryParams.toString()
    });
  };

  const handleSvgUpload = (file) => {
    if(!file) {
      setSvgFile(null);
      setSvgContent(null);
      return;
    };

    setSvgFile(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
          const svgContent = processSvgContent(e.target.result, 'darkred');
          const {width, height, paths} = getSvgDimensionsAndPaths(svgContent);
          setSvgContent(svgContent ?? null);
          dispatch({
            type: 'UPDATE_PROFILE_CONFIG',
            payload: {
              profileId: profileSku,
              configs: {
                ...state.profileConfigs[profileSku],
                [widthSku]: {
                  ...widthConfig,
                  config: {
                    ...widthConfig.config,
                    [configSwitchLabel]: {
                      ...widthConfig.config[configSwitchLabel],
                      width,
                      height,
                      path: paths?.[0] || config?.path || ""
                    }
                  }
                }
              }
            },
          });
      } catch (error) {
        console.error('Error processing SVG:', error);
      }
    };

    reader.readAsText(file);
  };

  const backgroundPatternId = useMemo(
    () => `ring-background-${widthMapping?.width}-${profileSku}`,
    [widthMapping, profileSku],
  );

  const getTransformConfig = useCallback((wConfig) => {
    if (!wConfig) return "";
    const { rotation, x, y } = wConfig;
    return `
        rotate(${-rotation})
        translate(${x}, ${-y})
    `;
  }, []);

  const handleSaveConfig = () => {
    console.log('widthConfig', widthConfig);
    const UPDATED_CONFIG = {
      ...widthConfig.config,
      base_config: {
        ...widthConfig.config.base_config,
        manualWarp: undefined,
      },
      inside_config: {
        ...widthConfig.config.inside_config,
        manualWarp: undefined,
      }
    }
    updateProfileConfig(widthConfig.id, profileSku, Number(widthSku), UPDATED_CONFIG);
  }

  const handleBack = () => {
    history.push('/design-pattern-studio');
  }

  const handlePositionChange = (x, y) => dispatch({
    type: 'UPDATE_PROFILE_CONFIG',
    payload: {
      profileId: profileSku,
      configs: {
        ...state.profileConfigs[profileSku],
        [widthSku]: {
          ...widthConfig,
          config: {
            ...widthConfig.config,
            [configSwitchLabel]: {
              ...widthConfig.config[configSwitchLabel],
              x, y
            }
          }
        }
      }
    }
  });

  const renderPreview = () => {
    if (!svgFile || !svgContent) return null;
    const pathTransformX = (SVG_SIZE - config?.width) / 2;
    const pathTransformY = (SVG_SIZE - config?.height) / 2;
    return (
      <svg
        {...extractSvgAttributes(svgContent)}
        width="100%"
        height="100%"
        viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          border:'1px solid #CCC'
        }}
      >
        <defs>
          <pattern id={`pattern-${backgroundPatternId}`} width="100%" height="100%" patternUnits="userSpaceOnUse">
            <image
              id={backgroundPatternId}
              href={widthMapping?.base_ring_url}
              width={SVG_SIZE}
              height={SVG_SIZE}
              preserveAspectRatio="xMidYMid meet"
            />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill={`url(#pattern-${backgroundPatternId})`} />
        <g transform={getTransformConfig(config)} style={{ transformOrigin: 'center' }}>
          <g transform={`translate(${pathTransformX}, ${pathTransformY})`}>
            <path
              d={config?.path || ''}
              fill={"rgba(208, 240, 190, 0.2)"}
              stroke="red"
              strokeWidth={1}
            />
          </g>
        </g>
      </svg>
    );
  }

  return (
    <ThemeProvider theme={customTheme}>
      <Box className={classes.root}>
        {source === 'studio' && <Button startIcon={<ArrowBackIcon />} onClick={handleBack} className={classes.backButton}>
          Back to Studio
        </Button>}
        <Typography variant="h4" className={classes.title}>
        Edit Configuration
        </Typography>
      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Box display="flex" flexDirection="column">
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                    <Typography variant="h6" className={classes.title}>Select Profile</Typography>
                    <Autocomplete
                      id="profile-config-autocomplete"
                      className={classes.dropdown}
                      value={availableProfiles.find(p => p.sku_code === profileSku) || null}
                      onChange={(e, value) => handleProfileChange(value?.sku_code || '')}
                      options={availableProfiles || []}
                      getOptionLabel={(option) => option?.name || ''}
                      filterOptions={(options, { inputValue }) => {
                        const searchTerms = inputValue.toLowerCase().split(' ');
                        return options.filter((option) => {
                          const searchString = `${option?.name || ''}`.toLowerCase();
                          return searchTerms.every((term) => searchString.includes(term));
                        });
                      }}
                      loading={loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search by profile name"
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                      renderOption={(option) => (
                        <div className={classes.optionText}>
                          <span>{option?.name || ''}</span>
                        </div>
                      )}
                    />
            </Box>
          {availableWidths.length > 0 && <><Typography variant="h6" className={classes.title}>Select Width</Typography>
            <ButtonGroup>
              {availableWidths.map((width) => (
                <Button
                  key={width}
                  onClick={() => handleWidthChange(width)}
                  className={width === Number(widthSku) ? classes.activeButton : ''}
                >
                  {width}
                  <Typography className={classes.widthUnit}>mm</Typography>
                </Button>
              ))}
            </ButtonGroup></>}
            <Box mt={2}>
              <Typography variant="h6" className={classes.title}>
                SVG Dimensions
              </Typography>
              <Box className={classes.svgDimensions} gap={2}>
                <TextField
                  type="number"
                  step={.01}
                  maxWidth={50}
                  label="Width"
                  value={config?.width || 0}
                  onChange={(e) => dispatch({
                    type: 'UPDATE_PROFILE_CONFIG',
                    payload: {
                      profileId: profileSku,
                      configs: {
                        ...state.profileConfigs[profileSku],
                        [widthSku]: {
                          ...widthConfig,
                          config: {
                            ...widthConfig.config,
                            [configSwitchLabel]: {
                              ...widthConfig.config[configSwitchLabel],
                              width: e.target.value
                            }
                          }
                        }
                      }
                  }
                })}
                  disabled={!svgFile}
                />
                <TextField
                  type="number"
                  step={.01}
                  maxWidth={50}
                  label="Height"
                  value={config?.height || 0}
                  onChange={(e) => dispatch({
                    type: 'UPDATE_PROFILE_CONFIG',
                    payload: {
                      profileId: profileSku,
                      configs: {
                        ...state.profileConfigs[profileSku],
                        [widthSku]: {
                          ...widthConfig,
                          config: {
                            ...widthConfig.config,
                            [configSwitchLabel]: {
                              ...widthConfig.config[configSwitchLabel],
                              height: e.target.value
                            }
                          }
                        }
                      }
                    }
                  })}
                  disabled={!svgFile}
                />
              </Box>
            </Box>
          </Box>
        </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="h6" className={classes.title}>
            Upload Engraving SVG
          </Typography>
          <SvgUpload file={svgFile} onFileChange={handleSvgUpload} />
        </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={2}>
            <Typography variant="h6" className={classes.title}>
                Rotation
            </Typography>
            <CircularRange
              value={config?.rotation || 0}
              onChange={(value) => dispatch({
                type: 'UPDATE_PROFILE_CONFIG',
                payload: {
                  profileId: profileSku,
                  configs: {
                    ...state.profileConfigs[profileSku],
                    [widthSku]: {
                      ...widthConfig,
                      config: {
                        ...widthConfig.config,
                        [configSwitchLabel]: {
                          ...widthConfig.config[configSwitchLabel],
                          rotation: value
                        }
                      }
                    }
                  }
                },
              })}
              disabled={!svgFile}
              size={120}
              strokeWidth={4}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" className={classes.title}>
              Position
            </Typography>
            <PositionGrid
              x={config?.x || 0}
              y={config?.y || 0}
              disabled={!svgFile}
              onPositionChange={handlePositionChange}
              markers={markersPositionGrid}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box ml={2}><Typography variant="h6" className={classes.title}>Preview</Typography></Box>
            <Box display="flex" flexDirection="row" justifyContent="space-around">
              <div className={classes.imagePreview}>
                {renderPreview()}
              </div>
            </Box>
          </Grid>
        </Grid>
        <Box mt={2} display="flex" justifyContent="center">
            <Button disabled={loading || !svgFile} variant="contained" color="primary" onClick={handleSaveConfig}>Save Configuration</Button>
        </Box>
      </Paper>
    </Box>
    </ThemeProvider>
  );
};

export default EditConfig;